import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';

import type { GetUserByRoleQuery, User } from '../../../gql';
import type { DocumentNode, TypedDocumentNode } from '@apollo/client';
import type { AutocompleteProps } from '@mui/material/Autocomplete';
import type { TextFieldProps } from '@mui/material/TextField';

import { useQuery } from '@apollo/client/react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { GetUserByRoleDocument } from '../../../gql';
import { GraphClientNames } from '../../../types';
import { isDefined } from '../../../utils';
import {
    generateOptionsArray,
    generatePlaceholder,
} from '../../../utils/inputHelpers';

/**
 * @param overwriteOptions - If given, these options will be used and the query for fetching collections will be skipped
 * @param overwriteQuery - If given, this query will be used for fetching collections data
 * @param textFieldProps - Props that will be passed to the TextField component used for the Autocomplete renderInput
 * @param brandNumber - The brand number which is used for fetching collections
 * @param roleNumber - If provided, only loads users with this role
 */
interface UsersAutocompleteProps<
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
> extends Omit<
        AutocompleteProps<
            // User: The type for the options and returned value(s)
            User,
            // Multiple: Can be set by the consuming Frontend
            Multiple,
            // DisableClearable: Can be set by the consuming Frontend
            DisableClearable,
            // FreeSolo: Must be false, otherwise value can be of type string
            false
        >,
        'freeSolo' | 'options' | 'renderInput'
    > {
    overwriteOptions?: Array<User>;
    overwriteQuery?: DocumentNode | TypedDocumentNode;
    textFieldProps?: TextFieldProps;
    brandNumber?: string;
    roleNumber?: string;
    sortingKey?: keyof User;
    includeDeleted?: boolean;
}

const fallbackDisplayName = 'Missing display name';

/**
 * @description This autocomplete can be used to select one or more users.
 * It's basically a wrapper around MUI Autocomplete, with some default values and styling.
 * If no options are given, it will fetch users autonomously.
 */
const UsersAutocomplete = <
    Multiple extends boolean = false,
    DisableClearable extends boolean = false,
>({
    overwriteOptions,
    overwriteQuery,
    textFieldProps = {},
    brandNumber,
    roleNumber,
    value,
    loading,
    multiple,
    limitTags,
    sortingKey,
    includeDeleted = true,
    onChange: handleChange,
    ...autoCompleteProps
}: UsersAutocompleteProps<Multiple, DisableClearable>): ReactElement => {
    const { data, loading: usersLoading } = useQuery<GetUserByRoleQuery>(
        overwriteQuery ?? GetUserByRoleDocument,
        {
            variables: {
                brandNumber,
                roleNumber,
                includeDeleted,
            },
            skip: !!overwriteOptions || !brandNumber,
            fetchPolicy: 'network-only',
            context: {
                clientName: GraphClientNames['bestone-bi4-buying-user-bff'],
            },
        }
    );

    const usersOptions: Array<User> = useMemo(
        () =>
            generateOptionsArray(
                overwriteOptions,
                data?.usersByRole.filter(isDefined),
                sortingKey as keyof NonNullable<
                    GetUserByRoleQuery['usersByRole']
                >[number]
            ),
        [overwriteOptions, data?.usersByRole, sortingKey]
    );

    const placeholder = useMemo(() => generatePlaceholder(value), [value]);

    return (
        <Autocomplete
            {...autoCompleteProps}
            options={usersOptions}
            getOptionLabel={(option): string =>
                option.displayName ?? fallbackDisplayName
            }
            value={value}
            loading={loading ?? usersLoading}
            loadingText="Loading users..."
            noOptionsText="No users found"
            renderInput={(params): ReactNode => (
                <TextField
                    {...params}
                    label={multiple ? 'Users' : 'User'}
                    placeholder={
                        params.slotProps.htmlInput['aria-expanded']
                            ? ''
                            : placeholder
                    }
                    {...textFieldProps}
                    slotProps={{
                        ...params.slotProps,
                        ...textFieldProps.slotProps,
                        input: {
                            ...params.slotProps.input,
                            ...(textFieldProps.slotProps?.input as object),
                        },
                        htmlInput: {
                            ...params.slotProps.htmlInput,
                            ...(textFieldProps.slotProps?.htmlInput as object),
                        },
                    }}
                />
            )}
            freeSolo={false}
            multiple={multiple}
            disableCloseOnSelect={multiple}
            blurOnSelect={!multiple}
            slotProps={{
                chip: { size: 'small' },
            }}
            openOnFocus
            fullWidth
            onChange={(event, newValue, reason, details): void => {
                handleChange?.(event, newValue, reason, details);
            }}
        />
    );
};

export { UsersAutocomplete };
